import React, { useState, useEffect } from "react";
import { Container, Card, Button, Spinner, Alert, Form } from "react-bootstrap";
import { useAuth } from "../contexts/AuthContext";
import { fetchUserAttributes } from "aws-amplify/auth";

export default function PendingApprovalPage() {
  const { user, logout } = useAuth();
  const [checking, setChecking] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  // Auto-refresh approval status every 10 seconds
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const interval = setInterval(async () => {
      try {
        console.log("Auto-checking approval status...");
        const attributes = await fetchUserAttributes();
        const approved = attributes["custom:approved"] === "true";

        if (approved) {
          console.log("User has been approved! Reloading page...");
          // Reload the page to trigger auth check and redirect
          window.location.reload();
        }
      } catch (error) {
        console.error("Error checking approval status:", error);
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [autoRefreshEnabled]);

  const checkApprovalStatus = async () => {
    try {
      setChecking(true);
      const attributes = await fetchUserAttributes();
      const approved = attributes["custom:approved"] === "true";

      if (approved) {
        // Reload the page to trigger auth check and redirect
        window.location.reload();
      } else {
        alert(
          "Your account is still pending approval. Please wait for an admin to approve your request."
        );
      }
    } catch (error: any) {
      console.error("Error checking approval:", error);
      alert("Failed to check approval status. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <Container className="mt-5">
      <div className="d-flex justify-content-center">
        <Card style={{ maxWidth: "600px", width: "100%" }}>
          <Card.Header className="bg-warning text-dark">
            <h4 className="mb-0">Account Pending Approval</h4>
          </Card.Header>
          <Card.Body>
            <Alert variant="info" className="mb-4">
              <Alert.Heading>
                Welcome, {user?.firstName || user?.email}!
              </Alert.Heading>
              <p className="mb-0">
                Your account has been created successfully, but it needs to be
                approved by an administrator before you can access the
                application.
              </p>
            </Alert>

            <div className="mb-4">
              <h5>What happens next?</h5>
              <ol>
                <li className="mb-2">
                  <strong>Admin Review:</strong> An administrator will review
                  your account request and assign you a role and permissions.
                </li>
                <li className="mb-2">
                  <strong>Email Notification:</strong> You'll receive an email
                  when your account is approved.
                </li>
                <li className="mb-2">
                  <strong>Access Granted:</strong> Once approved, you'll be able
                  to sign in and use the application.
                </li>
              </ol>
            </div>

            <hr />

            <div className="d-flex justify-content-between align-items-center mt-4">
              <div>
                <Form.Check
                  type="checkbox"
                  label="Auto-refresh (checks every 10 seconds)"
                  checked={autoRefreshEnabled}
                  onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                />
                {autoRefreshEnabled && (
                  <small className="text-muted d-block mt-1">
                    Automatically checking for approval...
                  </small>
                )}
              </div>
              <div>
                <Button
                  variant="outline-secondary"
                  onClick={logout}
                  className="me-2"
                >
                  Sign Out
                </Button>
                <Button
                  variant="primary"
                  onClick={checkApprovalStatus}
                  disabled={checking}
                >
                  {checking ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Checking...
                    </>
                  ) : (
                    "Check Approval Status"
                  )}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
}
